import * as moment from "moment";
import { ThunkAction } from "redux-thunk";
import { loadCasesStart, loadCasesError } from "../../actions/load-cases";
import { ICase, ICasesResponse, ISelectedFilters } from "../../state/cases";
import { Http } from "@telstra-connect/utils";
import { filterCasesBySearch } from "./filter-cases-by-search";
import { IChannelCareAppState } from "@telstra-connect/channel-care/state/index";

/**
 * This thunk action loads cases based on the selected filters and updates the Redux store with the results.
 * It configures filters from the selected filter and application state, performs an API call to fetch cases,
 * and dispatches actions to update the store with the filtered cases and support filters.
 *
 * @param selectedFilter - An object containing the filters selected by the user, including status, dates,
 * submitted by, and assigned to fields.
 * @param selectedCompanyId - An optional company ID used for custom headers in the API request.
 *
 * @Behavior
 * - It retrieves the current application state to configure filters for the API request.
 * - Dispatches `loadCasesStart` to indicate that case loading has begun.
 * - Makes an HTTP GET request to fetch cases based on the configured filters.
 * - On successful retrieval, it filters the cases based on additional criteria and dispatches actions to update
 * both the filtered cases and support filters in the Redux store.
 * - In case of an error during fetching, it dispatches `loadCasesError` with an appropriate error message.
 */
export function loadCasesFilterUplift(
    selectedFilter: ISelectedFilters,
    selectedCompanyId: string,
    updateSupportFilter?: boolean
): ThunkAction<void, IChannelCareAppState, void> {
    return (dispatch, getState) => {
        const appState: IChannelCareAppState = getState();
        const filters = configureFilters(selectedFilter, appState);
        dispatch(loadCasesStart());
        return Http.get<ICasesResponse>({
            url: "/ccd/v1/cases",
            queryString: {
                status: filters.status,
                fromDate: filters.fromDate,
                toDate: filters.toDate,
            },
            state: getState(),
            customHeaders: {"company-id": selectedCompanyId},
        })
            .then((result) => {
                const triggerTime = new Date();
                const cases =
                    result && Array.isArray(result.cases) ? result.cases : [];
                const searchText: string =
                    appState.channelCare.selectedFilters?.searchText;
                const filteredCases: ICase[] = filterCases(filters, cases);
                dispatch(
                    filterCasesBySearch(searchText, {
                        original_cases: filteredCases,
                        fetchTime: triggerTime.getTime(),
                        selectedCompanyId,
                        shouldUpdateSupportFilter: !!updateSupportFilter,
                    })
                );
            })
            .catch(() => {
                dispatch(loadCasesError("Error loading cases list"));
            });
    };
}

function filterCases(filters, cases: ICase[]): ICase[] {
    const hasSubmittedByFilters = filters.submittedBy && filters.submittedBy?.length > 0;
    const hasAssignedToFilters = filters.assignedTo && filters.assignedTo?.length > 0;
    if (!hasSubmittedByFilters && !hasAssignedToFilters) {
        return cases;
    }
    let filteredCases: ICase[] = [];
    if (hasSubmittedByFilters) {
        const filterResult = cases.filter((item: ICase) => {
            return filters.submittedBy.includes(item.submittedBy);
        });
        filteredCases = [...filteredCases, ...filterResult];
    }
    if (hasAssignedToFilters) {
        const filterResult = cases.filter((item) => {
            return filters.assignedTo.includes(
                item.subStatus === "-" ? "N/A" : item.subStatus
            );
        });
        filteredCases = [...filteredCases, ...filterResult];
    }
    return filteredCases;
}

function configureFilters(selectedFilter, appState) {
    return {
        status:
            selectedFilter?.status.toString() ||
            appState.channelCare.selectedFilters.status?.toString(),
        assignedTo:
            selectedFilter?.assignedTo.toString() ||
            appState.channelCare.selectedFilters.assignedTo?.toString(),
        fromDate:
            selectedFilter?.dates.length > 0
                ? moment(selectedFilter?.dates[0])
                    .utc()
                    .format("YYYY-MM-DDTHH:mm:ss[Z]")
                : appState.channelCare.selectedFilters.dates.length > 0
                    ? moment(appState.channelCare.selectedFilters.dates[0])
                        .utc()
                        .format("YYYY-MM-DDTHH:mm:ss[Z]")
                    : "",
        toDate:
            selectedFilter?.dates.length > 0
                ? moment(selectedFilter?.dates[1])
                    .utc()
                    .format("YYYY-MM-DDTHH:mm:ss[Z]")
                : appState.channelCare.selectedFilters.dates.length > 0
                    ? moment(appState.channelCare.selectedFilters.dates[1])
                        .utc()
                        .format("YYYY-MM-DDTHH:mm:ss[Z]")
                    : "",
        submittedBy:
            selectedFilter?.submittedBy.length > 0
                ? selectedFilter?.submittedBy
                : appState.channelCare.selectedFilters.submittedBy,
    };
}

function hasNoFilter(selectedFilters: ISelectedFilters) {
    return selectedFilters.assignedTo
}
